import { type RefObject, useLayoutEffect, useState } from "react";

type TruncateMiddleLayout = {
  startWidth: number | undefined;
  endWidth: number | undefined;
  minWidth: number | undefined;
};

const EMPTY_LAYOUT: TruncateMiddleLayout = {
  startWidth: undefined,
  endWidth: undefined,
  minWidth: undefined,
};

// The islands bridge mounts through a `display: contents` wrapper, which has
// no box at all and so reports a width of zero. Step past those, and past the
// shadow boundary, to the first ancestor that actually generates one.
function measurableParent(el: HTMLElement): HTMLElement | null {
  let node: Node | null = el.parentNode;

  while (node) {
    if (node instanceof HTMLElement) {
      if (getComputedStyle(node).display !== "contents") {
        return node;
      }
    }

    node = node instanceof ShadowRoot ? node.host : node.parentNode;
  }

  return null;
}

export function useTruncateMiddleLayout(
  rootRef: RefObject<HTMLSpanElement | null>,
  startRef: RefObject<HTMLSpanElement | null>,
  endRef: RefObject<HTMLSpanElement | null>,
  floorStartRef: RefObject<HTMLSpanElement | null>,
  floorEndRef: RefObject<HTMLSpanElement | null>,
  text: string,
  minChars: number,
): TruncateMiddleLayout {
  const [layout, setLayout] = useState<TruncateMiddleLayout>(EMPTY_LAYOUT);

  useLayoutEffect(() => {
    const root = rootRef.current;
    const container = root && measurableParent(root);

    if (!container) {
      return;
    }

    const recompute = () => {
      const start = startRef.current;
      const end = endRef.current;
      const floorStart = floorStartRef.current;
      const floorEnd = floorEndRef.current;

      if (!start || !end || !floorStart || !floorEnd) {
        return;
      }

      const naturalStart = start.scrollWidth;
      const naturalEnd = end.scrollWidth;
      const floorStartWidth = floorStart.scrollWidth;
      const floorEndWidth = floorEnd.scrollWidth;
      const minWidth = floorStartWidth + floorEndWidth;
      const available = container.clientWidth;
      const fullWidth = naturalStart + naturalEnd;

      if (available >= fullWidth) {
        setLayout({ startWidth: undefined, endWidth: undefined, minWidth });
        return;
      }

      const deficit = fullWidth - available;
      const startBudget = Math.max(0, naturalStart - floorStartWidth);
      const startGiveUp = Math.min(deficit, startBudget);
      const remaining = deficit - startGiveUp;
      const endBudget = Math.max(0, naturalEnd - floorEndWidth);
      const endGiveUp = Math.min(remaining, endBudget);

      setLayout({
        startWidth: naturalStart - startGiveUp,
        endWidth: remaining > 0 ? naturalEnd - endGiveUp : undefined,
        minWidth,
      });
    };

    recompute();

    const observer = new ResizeObserver(recompute);

    observer.observe(container);

    return () => observer.disconnect();
  }, [rootRef, startRef, endRef, floorStartRef, floorEndRef, text, minChars]);

  return layout;
}
